import Swiper from "swiper";
import { A11y, Autoplay, Navigation } from "swiper/modules";
/**
 * Wspólne ustawienia wszystkich karuzel.
 */
const getCommonSettings = ({ slideCount, reducedMotion, }) => ({
    modules: [A11y, Autoplay, Navigation],
    slidesPerGroup: 1,
    speed: reducedMotion ? 0 : 750,
    loop: slideCount > 1,
    loopAddBlankSlides: false,
    watchOverflow: true,
    grabCursor: slideCount > 1,
    simulateTouch: true,
    a11y: {
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
        slideLabelMessage: "{{index}} / {{slidesLength}}",
    },
});
/**
 * Galerie — liczba kart zależna od szerokości kontenera.
 */
const getGallerySettings = ({ slideCount, reducedMotion, }) => {
    const visible = (desired) => Math.min(desired, Math.max(1, slideCount - 1));
    return {
        slidesPerView: visible(1.15),
        spaceBetween: 18,
        autoHeight: false,
        breakpointsBase: "container",
        breakpoints: {
            560: {
                slidesPerView: visible(1.7),
                spaceBetween: 24,
            },
            900: {
                slidesPerView: visible(2.5),
                spaceBetween: 28,
            },
            1280: {
                slidesPerView: visible(3.25),
                spaceBetween: 28,
            },
        },
        autoplay: reducedMotion || slideCount < 2
            ? false
            : {
                delay: 4500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
    };
};
/**
 * Testimonials — zawsze jedna opinia na widoku.
 */
const getTestimonialSettings = ({ slideCount, reducedMotion, }) => ({
    slidesPerView: 1,
    slidesPerGroup: 1,
    spaceBetween: 0,
    autoHeight: true,
    autoplay: reducedMotion || slideCount < 2
        ? false
        : {
            delay: 7000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
});
/**
 * Przycisk nawigacji.
 */
const createArrow = (direction) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
        `theme-carousel__arrow theme-carousel__arrow--${direction}`;
    button.setAttribute("aria-label", direction === "prev" ? "Previous slide" : "Next slide");
    button.innerHTML = `
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 12h15M13 5l7 7-7 7"/>
    </svg>
  `;
    return button;
};
/**
 * Nie tworzy kontrolek, jeśli strzałki są wyłączone.
 */
const createControls = (host, enabled) => {
    if (!enabled)
        return null;
    const controls = document.createElement("div");
    controls.className = "theme-carousel__controls";
    const previous = createArrow("prev");
    const next = createArrow("next");
    controls.append(previous, next);
    host.append(controls);
    return { previous, next };
};
/**
 * Zatrzymanie autoplay podczas korzystania z klawiatury.
 */
const bindFocusEvents = (host, swiper, autoplayEnabled) => {
    host.addEventListener("focusin", () => {
        swiper.autoplay?.stop();
    });
    host.addEventListener("focusout", (event) => {
        const target = event.relatedTarget;
        if (target instanceof Node && host.contains(target))
            return;
        if (autoplayEnabled) {
            swiper.autoplay?.start();
        }
    });
};
/**
 * Inicjalizacja pojedynczej karuzeli.
 */
const initializeCarousel = (container, reducedMotion) => {
    if (container.dataset.swiperReady === "true")
        return;
    const testimonial = container.hasAttribute("data-testimonials");
    const slides = Array.from(container.children).filter((child) => child instanceof HTMLElement);
    if (!slides.length)
        return;
    container.dataset.swiperReady = "true";
    const context = {
        slideCount: slides.length,
        reducedMotion,
    };
    const showArrows = container.dataset.carouselArrows !== "false" &&
        slides.length > 1;
    const host = document.createElement("div");
    host.className = "theme-carousel";
    if (testimonial) {
        host.classList.add("theme-carousel--testimonials");
    }
    container.before(host);
    host.append(container);
    container.classList.add("swiper", "theme-carousel__viewport");
    container.setAttribute("role", "region");
    container.setAttribute("aria-label", testimonial ? "Recommendations" : "Gallery");
    const wrapper = document.createElement("div");
    wrapper.className = "swiper-wrapper";
    slides.forEach((slide) => {
        slide.classList.add("swiper-slide");
        slide.querySelectorAll("img").forEach((image) => {
            image.draggable = false;
        });
        wrapper.append(slide);
    });
    container.append(wrapper);
    const controls = createControls(host, showArrows);
    const settings = testimonial
        ? getTestimonialSettings(context)
        : getGallerySettings(context);
    const autoplayEnabled = Boolean(settings.autoplay) && !reducedMotion;
    new Swiper(container, {
        ...getCommonSettings(context),
        ...settings,
        navigation: controls
            ? {
                prevEl: controls.previous,
                nextEl: controls.next,
                addIcons: false,
            }
            : false,
        on: {
            init(swiper) {
                bindFocusEvents(host, swiper, autoplayEnabled);
            },
        },
    });
};
/**
 * Punkt wejścia.
 */
export const initializeCarousels = () => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const containers = document.querySelectorAll([
        ".scroll_list .post_query",
        ".post_query-projects.horizontal",
        "[data-testimonials]",
    ].join(", "));
    containers.forEach((container) => {
        initializeCarousel(container, reducedMotion);
    });
};
